import { sql } from "drizzle-orm";
import { pgTable, varchar, text, timestamp, integer, boolean, jsonb, index, uniqueIndex, check } from "drizzle-orm/pg-core";

const base = () => ({
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export const clientIntakeSubmissions = pgTable("client_intake_submissions", {
  ...base(),
  status: text("status").notNull().default("draft"),
  submitted_at: timestamp("submitted_at", { withTimezone: true }),
  token_hash: text("token_hash"), brand_id: varchar("brand_id"), edit_secret_hash: text("edit_secret_hash").notNull(),
  smb_order_id: varchar("smb_order_id"),
  ip_hash: text("ip_hash"), user_agent: varchar("user_agent", { length: 200 }),
  business_name: text("business_name"), contact_name: text("contact_name"),
  contact_email: text("contact_email"), contact_phone: text("contact_phone"),
  website_url: text("website_url"), no_website: boolean("no_website"), open_to_new_domain: boolean("open_to_new_domain"),
  domain_no_preference: boolean("domain_no_preference"), domain_ending: text("domain_ending"),
  domain_ending_other: text("domain_ending_other"), existing_domain: text("existing_domain"), industry: text("industry"), industry_other: text("industry_other"),
  tone: text("tone").array(), tone_other: text("tone_other"), avoid_text: text("avoid_text"), credentials_text: text("credentials_text"), no_liked_sites: boolean("no_liked_sites"),
  address: text("address"), service_radius: text("service_radius"), service_radius_other: text("service_radius_other"),
  cities_text: text("cities_text"), excluded_areas_text: text("excluded_areas_text"), icp_description: text("icp_description"),
  age_ranges: text("age_ranges").array(), pain_points: text("pain_points").array(), why_choose_us: text("why_choose_us"),
  job_value: text("job_value"), languages: text("languages"), languages_other: text("languages_other"), competitor_notes: text("competitor_notes"),
  facebook_url: text("facebook_url"), instagram_url: text("instagram_url"), tiktok_url: text("tiktok_url"),
  google_business_url: text("google_business_url"), yelp_url: text("yelp_url"), youtube_url: text("youtube_url"),
  runs_ads: boolean("runs_ads"), ad_platforms: text("ad_platforms").array(), ad_platforms_other: text("ad_platforms_other"),
  ad_monthly_spend: text("ad_monthly_spend"), ad_what_worked: text("ad_what_worked"),
  meta_account_status: text("meta_account_status"), meta_account_id: text("meta_account_id"), manage_account_for_me: boolean("manage_account_for_me"),
  has_facebook_page: boolean("has_facebook_page"), tiktok_account_status: text("tiktok_account_status"), pixel_status: text("pixel_status"), monthly_budget: text("monthly_budget"),
  goal_overall: text("goal_overall"), goal_website: text("goal_website"), goal_static_ads: text("goal_static_ads"), goal_video_ads: text("goal_video_ads"), goal_more: text("goal_more"),
  main_goal: text("main_goal"), main_goal_other: text("main_goal_other"), monthly_lead_target: integer("monthly_lead_target"), busy_months: text("busy_months").array(),
}, t => [
  index("client_intake_token_idx").on(t.token_hash),
  index("client_intake_status_created_idx").on(t.status, t.createdAt),
  uniqueIndex("client_intake_smb_order_unique").on(t.smb_order_id).where(sql`${t.smb_order_id} is not null`),
  check("client_intake_status_check", sql`${t.status} IN ('draft','submitted')`),
]);
const child = () => ({
  ...base(), submission_id: varchar("submission_id").notNull().references(() => clientIntakeSubmissions.id, { onDelete: "cascade" }),
  position: integer("position").notNull(),
});
export const clientIntakeServices = pgTable("client_intake_services", {
  ...child(), name: text("name"), description: text("description"), starting_price: text("starting_price"), is_top_priority: boolean("is_top_priority"),
}, t => [index("client_intake_services_submission_idx").on(t.submission_id)]);
export const clientIntakePromotions = pgTable("client_intake_promotions", {
  ...child(), offer: text("offer"), dates: text("dates"), terms: text("terms"),
}, t => [index("client_intake_promotions_submission_idx").on(t.submission_id)]);
export const clientIntakeCompetitors = pgTable("client_intake_competitors", {
  ...child(), name: text("name"), url: text("url"),
}, t => [index("client_intake_competitors_submission_idx").on(t.submission_id)]);
export const clientIntakeLikedSites = pgTable("client_intake_liked_sites", {
  ...child(), url: text("url"), note: text("note"),
}, t => [index("client_intake_liked_sites_submission_idx").on(t.submission_id)]);
export const clientIntakeDomainIdeas = pgTable("client_intake_domain_ideas", {
  ...child(), name: text("name"),
}, t => [index("client_intake_domain_ideas_submission_idx").on(t.submission_id)]);
export const clientIntakeBrandColors = pgTable("client_intake_brand_colors", {
  ...child(), hex: text("hex"), label: text("label"),
}, t => [index("client_intake_brand_colors_submission_idx").on(t.submission_id)]);
export const clientIntakeAssets = pgTable("client_intake_assets", {
  ...child(), kind: text("kind").notNull(), r2_key: text("r2_key").notNull().unique(),
  original_filename: text("original_filename").notNull(), content_type: text("content_type").notNull(),
  size_bytes: integer("size_bytes").notNull(), status: text("status").notNull().default("pending"),
}, t => [
  index("client_intake_assets_submission_idx").on(t.submission_id),
  check("client_intake_asset_kind_check", sql`${t.kind} IN ('logo','photo')`),
  check("client_intake_asset_status_check", sql`${t.status} IN ('pending','confirmed')`),
  check("client_intake_asset_size_check", sql`${t.size_bytes} > 0 AND ${t.size_bytes} <= 15728640`),
]);
export type ClientIntakeSubmission = typeof clientIntakeSubmissions.$inferSelect;
export type ClientIntakeAsset = typeof clientIntakeAssets.$inferSelect;
export const clientIntakeLinks = pgTable("client_intake_links", {
  ...base(),
  token_hash: text("token_hash").notNull().unique(),
   token_encrypted: text("token_encrypted"),
  token_last4: varchar("token_last4", { length: 4 }).notNull(),
  brand_id: varchar("brand_id"),
  submission_id: varchar("submission_id").references(() => clientIntakeSubmissions.id, { onDelete: "set null" }),
  label: text("label"), contact_name: text("contact_name"), contact_email: text("contact_email"),
  contact_phone: text("contact_phone"), staff_note: text("staff_note"),
  expires_at: timestamp("expires_at", { withTimezone: true }),
  revoked_at: timestamp("revoked_at", { withTimezone: true }),
  last_opened_at: timestamp("last_opened_at", { withTimezone: true }),
  created_by: varchar("created_by"),
}, t => [
  index("client_intake_links_submission_idx").on(t.submission_id),
  index("client_intake_links_brand_idx").on(t.brand_id),
  check("client_intake_links_note_check", sql`length(${t.staff_note}) <= 1000`),
]);
export const clientIntakeRevisions = pgTable("client_intake_revisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  submission_id: varchar("submission_id").notNull().references(() => clientIntakeSubmissions.id, { onDelete: "cascade" }),
  revision_number: integer("revision_number").notNull(),
  answers_hash: text("answers_hash").notNull(),
  snapshot: jsonb("snapshot").notNull(),
  source: text("source").notNull(),
  created_at: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, t => [
  uniqueIndex("client_intake_revisions_submission_number_idx").on(t.submission_id, t.revision_number),
  check("client_intake_revisions_number_check", sql`${t.revision_number} >= 1`),
  check("client_intake_revisions_source_check", sql`${t.source} IN ('initial','link_update')`),
]);
